import React from 'react';
import { Card, CardContent } from './ui/card';
import { Plane, Compass, FileText, Package, Home, MapPin } from 'lucide-react';

const Services = () => {
  const serviceCategories = [
    {
      icon: Plane,
      title: 'International Holiday Packages',
      description: 'Custom tour packages to Salalah, Dubai, Thailand, Malaysia, Singapore, and Austria',
      image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=85',
      highlights: ['Pick-up in Nizwa', 'Group Tours', 'Customized Itineraries'],
    },
    {
      icon: Compass,
      title: 'Hajj & Umrah Services',
      description: 'Complete pilgrimage packages with exceptional care and guidance',
      image: 'https://images.unsplash.com/photo-1720549973451-018d3623b55a?q=85',
      highlights: ['Expert Guidance', 'Accommodation', 'Transportation'],
    },
    {
      icon: FileText,
      title: 'Visa Processing',
      description: 'International visa assistance for tourist and business travel worldwide',
      image: 'https://images.pexels.com/photos/18341554/pexels-photo-18341554.jpeg?auto=compress&cs=tinysrgb&w=940',
      highlights: ['Tourist Visas', 'Business Visas', 'Fast Processing'],
    },
    {
      icon: Package,
      title: 'Ticketing & Insurance',
      description: 'Air & bus tickets with comprehensive travel insurance plans',
      image: 'https://images.unsplash.com/photo-1603477849227-705c424d1d80?q=85',
      highlights: ['Air Tickets', 'Bus Tickets', 'Travel Insurance'],
    },
    {
      icon: Home,
      title: 'Local Services',
      description: 'Holiday apartments, Oman tours, and airport transfers',
      image: 'https://images.unsplash.com/photo-1504257538320-8a5d5f1c0f31?q=85',
      highlights: ['Apartment Rentals', 'Airport Transfers', 'Local Tours'],
    },
    {
      icon: MapPin,
      title: 'Cargo Services',
      description: 'Reliable air and sea cargo services for your shipping needs',
      image: 'https://images.pexels.com/photos/36840682/pexels-photo-36840682.jpeg?auto=compress&cs=tinysrgb&w=940',
      highlights: ['Air Cargo', 'Sea Cargo', 'Door-to-Door'],
    },
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-[#1E7B8C]">Services</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Comprehensive travel solutions tailored to your needs—from international adventures to local convenience
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {serviceCategories.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card
                key={index}
                className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 right-4 w-14 h-14 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="h-7 w-7 text-[#1E7B8C]" />
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1E7B8C] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Highlights */}
                  <ul className="space-y-2">
                    {service.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 bg-[#F4B41A] rounded-full mr-2"></span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Special Note */}
        <div className="mt-16 max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-[#1E7B8C] to-[#2A9DAF] border-0 shadow-xl">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">
                🚗 Exclusive Nizwa Pick-up Service
              </h3>
              <p className="text-white/95 text-lg leading-relaxed">
                Enjoy the convenience of our exclusive pick-up service in Nizwa for all group tours and packages. 
                Travel comfortably from your doorstep!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Services;